
/**
 * Available languages for Ajokaista
 */
const languages = ['FI', 'SE', 'EN', 'RU'];

/**
 * Array of objects for question literals
 */
const textParts = [
  { key: 'question', label: 'Kysymys', part: 'Q', requiredLanguages: ['FI'], validationKey: 'validation_question_required' },
  { key: 'optionA', label: 'Vaihtoehto A', part: 'A', requiredLanguages: ['FI'], validationKey: 'validation_option_a_required' },
  { key: 'optionB', label: 'Vaihtoehto B', part: 'B', requiredLanguages: ['FI'], validationKey: 'validation_option_b_required' },
  { key: 'optionC', label: 'Vaihtoehto C', part: 'C', requiredLanguages: ['FI'], validationKey: 'validation_option_c_required' },
  { key: 'explanation', label: 'Selite', part: 'X' }
];

/**
 * Structure for question edit modal
 * TODO: Localizations for labels
 */
const formStructure = {
  literal: [
    { name: 'notes', label: 'Muistiinpanot', type: 'text', disabled: false },
    { name: 'id', label: 'ID', type: 'text', disabled: true, onlyEditMode: true },
    { name: 'needsReview', label: 'Tarkistettava', type: 'checkbox', disabled: false },
    { name: 'needsLocalization', label: 'Tarvitsee käännöksiä', type: 'checkbox', disabled: false },
    { name: 'inUse', label: 'Aktiivinen', type: 'checkbox', disabled: false },
    { name: 'correctAnswer', label: 'Oikea vastaus', type: 'text', disabled: false, 
      validation: {
        required: { enabled: true, errorKey: 'validation_correct_answer_required' },
        pattern: { values: ['A', 'B', 'C'], errorKey: 'validation_correct_answer_invalid' }
      }
    },
    { name: 'isRisk', label: 'Riskikysymys', type: 'checkbox', disabled: false },
    { name: 'imageUrl', label: 'Kuvan URL', type: 'text', disabled: false },
    { name: 'topic', label: 'Aihe', type: 'text', disabled: false },
    { name: 'categoryM', label: 'AM120', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryAM', label: 'AM121', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryA', label: 'A', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryB', label: 'B', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryBE', label: 'BE', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryC1', label: 'C1', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryC1E', label: 'C1E', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryC', label: 'C', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryCE', label: 'CE', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryD1', label: 'D1', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryD1E', label: 'D1E', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryD', label: 'D', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryDE', label: 'DE', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryT', label: 'T', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryK', label: 'K', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryL', label: 'L', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryTAXI', label: 'TAXI', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryADR', label: 'ADR', type: 'checkbox', disabled: false, group: 'category' },
    { name: 'categoryCPC', label: 'AMP', type: 'checkbox', disabled: false, group: 'category' }
  ],
  literalTexts: createLiteralTexts()
};

/**
 * Returns array of form objects
 * @returns {Array<Object>}
 */
export const getFormFields = (editMode) =>
  [
    ...formStructure.literal.filter(f => !f.onlyEditMode || editMode),
    ...formStructure.literalTexts
  ];

/**
 * Builds default form data object
 * @returns {Object} Default form data object
 */
export const getDefaultFormData = () => {
  const defaultFormData = {};

  formStructure.literal.forEach(field => {
    defaultFormData[field.name] = field.type === 'checkbox' ? false : '';
  });

  formStructure.literalTexts.forEach(field => {
    defaultFormData[field.name] = '';
  });

  return defaultFormData;
};

/**
 * Builds dynamic form data with backend values
 * @param {Object} questionData 
 * @returns {Object}
 */
export const getFormData = (questionData) => {
  const newFormData = {};

  formStructure.literal.forEach(field => {
    const dynamicValue = questionData.literal?.[field.name];
    const defaultValue = field.type === 'checkbox' ? false : '';
    newFormData[field.name] = dynamicValue ?? defaultValue;
  });

  formStructure.literalTexts.forEach(field => {
    const item = questionData.literalTexts?.find(item => item.questionPart === field.questionPart);
    newFormData[field.name] = item?.[field.lang] ?? '';
  });

  return newFormData;
};

/**
 * Creates literal text objects for building form
 * @returns {Array<Object>} Array of text field objects.
 */
function createLiteralTexts() {

  const literalTexts = [];

  languages.forEach(lang => {
    textParts.forEach(part => {
      literalTexts.push({
        name: `${part.key}${lang}`,
        label: `${part.label} (${lang.toUpperCase()})`,
        type: 'textarea',
        disabled: false,
        questionPart: part.part,
        lang: `lang${lang}`
      });
    });
  });
  return literalTexts;
};

/**
 * Creates payload
 * @param {Object} formData 
 * 
 * @returns {Object}
 */
export const getRequestPayload = (formData) => {

  const payload = {
    literal: {},
    literalTexts: [],
    bookReferences: [],
    id: formData.id
  }

  payload.literal = createPayloadLiteral(formData);
  payload.literalTexts = createPayloadLiteralTexts(formData);

  return payload;
}

/**
 * Creates literal data from form data for payload
 * @param {Object} formData 
 * @returns {Object}
 */
function createPayloadLiteral(formData) {

  const literalObject = {};

  formStructure.literal.forEach(item => {
    literalObject[item.name] = formData[item.name];
  });
  return literalObject;
}

/**
 * Creates literal text data from form for payload
 * @param {Object} formData 
 * @returns {Array<Object>}
 */
function createPayloadLiteralTexts(formData){

   const literalTextObjects = [];

   textParts.forEach(part => {
      const item = {
        id: formData.id,
        questionPart: part.part,
      };
      languages.forEach(lang => {
        const formKey = `${part.key}${lang}`;
        item[`lang${lang}`] = formData[formKey];
      });

      literalTextObjects.push(item);
   });
   return literalTextObjects;
}

/**
 * Validates form data
 * @param {Object} formData 
 * @returns {Object} { isValid: boolean, errors: Object<string, string> }
 */
export const validateFormData = (formData, assessment = null) => {
  const errors = {};

  // Validate literal fields based on formStructure
  formStructure.literal.forEach(field => {
    if (field.validation) {
      const value = formData[field.name];
      
      // Required validation
      if (field.validation.required?.enabled) {
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          errors[field.name] = field.validation.required.errorKey;
        }
      }
      
      // Pattern validation (for specific allowed values)
      if (field.validation.pattern && value && typeof value === 'string' && value.trim() !== '') {
        if (!field.validation.pattern.values.includes(value)) {
          errors[field.name] = field.validation.pattern.errorKey;
        }
      }
    }
  });

  // Validate literal text fields based on textParts
  textParts.forEach(part => {
    if (part.requiredLanguages) {
      part.requiredLanguages.forEach(lang => {
        const formKey = `${part.key}${lang}`;
        if (!formData[formKey] || formData[formKey].trim() === '') {
          errors[formKey] = part.validationKey;
        }
      });
    }
  });

  if (assessment) {
    if (!assessment.topics?.some(topic => topic.id === formData.topic)) {
      errors.topic = assessment.validationKeys?.topicRequired;
    } else if (formData.isRisk && assessment.validationKeys?.riskNotAllowed) {
      errors.topic = assessment.validationKeys.riskNotAllowed;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

